import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  GraduationCap, Clock, Loader2, ChevronLeft, ChevronRight,
  Check, X, RotateCcw, AlertTriangle, Brain, Zap,
  BarChart3, ArrowRight, Crown, Lock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExamMode = ({ documentId, courseId, user }) => {
  const [step, setStep] = useState('config'); // config | generating | exam | results
  const [config, setConfig] = useState({ num_questions: 20, timed: false, time_limit_minutes: 30, difficulty: 'mixed' });
  const [examId, setExamId] = useState(null);
  const [exam, setExam] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [limits, setLimits] = useState(null);
  const timerRef = useRef(null);
  const pollingRef = useRef(null);

  useEffect(() => {
    fetchHistory();
    fetchLimits();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [documentId, courseId]);

  const fetchLimits = async () => {
    try {
      const params = {};
      if (documentId) params.document_id = documentId;
      if (courseId) params.course_id = courseId;
      const res = await axios.get(`${API}/exam/limits`, { params });
      setLimits(res.data);
    } catch (e) { /* limits will show as unknown */ }
  };

  const fetchHistory = async () => {
    try {
      const endpoint = documentId
        ? `${API}/exam/history/document/${documentId}`
        : `${API}/exam/history/course/${courseId}`;
      const res = await axios.get(endpoint);
      setHistory(res.data || []);
    } catch (e) { /* no history yet */ }
  };

  const pollExamStatus = async (eid) => {
    try {
      const res = await axios.get(`${API}/exam/${eid}`);
      if (res.data.status === 'ready' || res.data.status === 'in_progress') {
        setExam(res.data);
        setStep('exam');
        fetchHistory();
        return;
      }
      if (res.data.status === 'completed') {
        setExam(res.data);
        setResults({
          score: res.data.score,
          correct: res.data.correct_count,
          total: res.data.total_questions,
          results: res.data.results || [],
          analysis: res.data.analysis || {},
          recommended_flashcards: res.data.recommended_flashcards || []
        });
        setStep('results');
        fetchHistory();
        return;
      }
      if (res.data.status === 'failed') {
        toast.error(res.data.error || 'Exam generation failed');
        setStep('config');
        fetchHistory();
        return;
      }
      pollingRef.current = setTimeout(() => pollExamStatus(eid), 3000);
    } catch (e) {
      pollingRef.current = setTimeout(() => pollExamStatus(eid), 3000);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      if (documentId) formData.append('document_id', documentId);
      if (courseId) formData.append('course_id', courseId);
      formData.append('num_questions', config.num_questions);
      formData.append('timed', config.timed);
      formData.append('time_limit_minutes', config.time_limit_minutes);
      formData.append('difficulty', config.difficulty);

      const res = await axios.post(`${API}/exam/generate`, formData);
      setExamId(res.data.exam_id);
      setStep('generating');
      pollExamStatus(res.data.exam_id);
      if (res.data.remaining >= 0) {
        setLimits(prev => prev ? { ...prev, remaining: res.data.remaining, can_generate: res.data.remaining > 0 } : prev);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate exam');
    } finally {
      setLoading(false);
    }
  };

  const handleStartExam = async () => {
    if (!examId) {
      toast.error('No exam selected. Please generate or select an exam first.');
      return;
    }
    // Immediately show questions - don't wait for API
    setExam(prev => {
      if (!prev) return prev;
      return { ...prev, status: 'in_progress' };
    });
    setCurrentQ(0);
    setAnswers({});

    // Start timer if timed
    if (exam?.config?.timed) {
      const mins = exam.config.time_limit_minutes || config.time_limit_minutes;
      setTimeLeft(mins * 60);
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            handleSubmitExam();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    // Fire-and-forget API call to record the start
    axios.post(`${API}/exam/${examId}/start`).catch(() => {});
  };

  const handleSubmitExam = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/exam/${examId}/submit`, { answers });
      setResults(res.data);
      setStep('results');
      fetchHistory();
      fetchLimits();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRetake = () => {
    setExam(null);
    setExamId(null);
    setResults(null);
    setAnswers({});
    setCurrentQ(0);
    setTimeLeft(null);
    setStep('config');
    fetchLimits();
  };

  const handleResumeExam = async (eid) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/exam/${eid}`);
      setExam(res.data);
      setExamId(eid);
      setConfig(res.data.config || config);
      if (res.data.status === 'completed') {
        setResults({
          score: res.data.score, correct: res.data.correct_count,
          total: res.data.total_questions, results: res.data.results || [],
          analysis: res.data.analysis || {}, recommended_flashcards: res.data.recommended_flashcards || []
        });
        setStep('results');
      } else if (res.data.status === 'generating') {
        setStep('generating');
        pollExamStatus(eid);
      } else {
        setStep('exam');
      }
    } catch (e) {
      toast.error('Failed to load exam');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      const formData = new FormData();
      formData.append('origin_url', window.location.origin);
      const res = await axios.post(`${API}/study-builder/subscribe`, formData);
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start subscription');
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const answeredCount = Object.keys(answers).length;
  const totalQuestions = exam?.questions?.length || 0;
  const canGenerate = limits?.can_generate !== false;
  const isPro = limits?.is_pro || false;

  // --- CONFIG SCREEN ---
  if (step === 'config') {
    return (
      <div className="p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg">
            <GraduationCap className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Crimson Text, serif' }}>
            Exam Mode
          </h3>
          <p className="text-sm text-gray-500 mt-1">Generate a realistic practice exam from your study materials</p>
          
          {/* Exam limit badge */}
          {limits && (
            <div className={`inline-flex items-center gap-1.5 mt-2 px-3 py-1 text-xs font-medium rounded-full border ${
              isPro
                ? 'bg-amber-50 text-amber-700 border-amber-200'
                : canGenerate
                  ? 'bg-violet-50 text-violet-700 border-violet-200'
                  : 'bg-red-50 text-red-700 border-red-200'
            }`}>
              {isPro ? (
                <><Crown className="w-3 h-3" /> Pro — Unlimited Exams</>
              ) : canGenerate ? (
                <><Zap className="w-3 h-3" /> {limits.remaining} of {limits.max_free} exams remaining</>
              ) : (
                <><Lock className="w-3 h-3" /> All {limits.max_free} exams used</>
              )}
            </div>
          )}
        </div>

        {/* Upgrade to Pro banner */}
        {limits && !canGenerate && !isPro && (
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 text-center">
            <Crown className="w-6 h-6 text-amber-600 mx-auto mb-2" />
            <p className="text-sm font-semibold text-amber-900 mb-1">Upgrade to Pro for Unlimited Exams</p>
            <p className="text-xs text-amber-700 mb-3">$10/month — unlimited exams + unlimited study dashboards</p>
            <Button
              data-testid="upgrade-pro-btn"
              onClick={handleSubscribe}
              className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 h-9 px-6 text-sm"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Pro
            </Button>
          </div>
        )}

        {canGenerate && (
          <div className="space-y-5 max-w-sm mx-auto">
            {/* Questions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Questions</label>
              <div className="grid grid-cols-3 gap-2">
                {[10, 20, 40].map(n => (
                  <button key={n} data-testid={`exam-q-${n}`}
                    onClick={() => setConfig({ ...config, num_questions: n })}
                    className={`p-3 rounded-xl border-2 text-center font-semibold transition-all ${
                      config.num_questions === n ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}>{n}</button>
                ))}
              </div>
            </div>

            {/* Difficulty */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'easy', label: 'Easy', active: 'border-green-500 bg-green-50 text-green-700' },
                  { value: 'mixed', label: 'Mixed', active: 'border-amber-500 bg-amber-50 text-amber-700' },
                  { value: 'hard', label: 'Hard', active: 'border-red-500 bg-red-50 text-red-700' },
                ].map(d => (
                  <button key={d.value} data-testid={`exam-diff-${d.value}`}
                    onClick={() => setConfig({ ...config, difficulty: d.value })}
                    className={`p-3 rounded-xl border-2 text-center font-medium transition-all ${
                      config.difficulty === d.value ? d.active : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}>{d.label}</button>
                ))}
              </div>
            </div>

            {/* Timer */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Timer</label>
              <div className="grid grid-cols-2 gap-2">
                <button data-testid="exam-untimed" onClick={() => setConfig({ ...config, timed: false })}
                  className={`p-3 rounded-xl border-2 text-center font-medium transition-all ${
                    !config.timed ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}>Untimed</button>
                <button data-testid="exam-timed" onClick={() => setConfig({ ...config, timed: true })}
                  className={`p-3 rounded-xl border-2 text-center font-medium transition-all ${
                    config.timed ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}><Clock className="w-4 h-4 inline mr-1" />Timed</button>
              </div>
              {config.timed && (
                <select data-testid="exam-time-select" value={config.time_limit_minutes}
                  onChange={e => setConfig({ ...config, time_limit_minutes: parseInt(e.target.value) })}
                  className="w-full mt-2 p-2 border rounded-lg text-sm">
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                  <option value={90}>90 minutes</option>
                </select>
              )}
            </div>

            <Button data-testid="generate-exam-btn" onClick={handleGenerate} disabled={loading}
              className="w-full rounded-xl bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800 h-12 text-base shadow-lg">
              {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <GraduationCap className="w-5 h-5 mr-2" />}
              Generate Practice Exam
            </Button>
          </div>
        )}

        {/* Pro upsell for non-pro users who still have exams */}
        {limits && canGenerate && !isPro && (
          <div className="mt-5 p-3 bg-gray-50 rounded-xl border border-gray-200 text-center">
            <p className="text-xs text-gray-500">
              <Crown className="w-3 h-3 inline mr-1 text-amber-500" />
              Want unlimited exams? <button onClick={handleSubscribe} className="text-violet-600 font-medium hover:underline">Upgrade to Pro — $10/mo</button>
            </p>
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="mt-8 border-t border-gray-200 pt-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />Past Exams
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {history.map(h => (
                <button key={h.id} data-testid={`exam-history-${h.id}`}
                  onClick={() => handleResumeExam(h.id)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-violet-50 transition-colors border border-gray-200 text-left">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{h.config?.num_questions || '?'} questions &middot; {h.config?.difficulty || 'mixed'}</p>
                    <p className="text-xs text-gray-500">{new Date(h.created_at).toLocaleDateString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {h.status === 'completed' ? (
                      <span className={`text-sm font-bold ${h.score >= 70 ? 'text-green-600' : h.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{h.score?.toFixed(0)}%</span>
                    ) : (
                      <span className="text-xs text-violet-600 font-medium">{h.status}</span>
                    )}
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- GENERATING ---
  if (step === 'generating') {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <div className="text-center p-6">
          <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
          </div>
          <h3 className="text-lg font-bold text-gray-800 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>Generating Your Exam...</h3>
          <p className="text-sm text-gray-500">AI is creating custom questions from your study materials</p>
          <p className="text-xs text-gray-400 mt-2">This may take 15-30 seconds</p>
        </div>
      </div>
    );
  }

  // --- EXAM TAKING ---
  if (step === 'exam' && exam) {
    const questions = exam.questions || [];
    const q = questions[currentQ];
    const started = exam.status === 'in_progress';

    if (!started) {
      return (
        <div className="flex items-center justify-center h-full min-h-[400px]">
          <div className="text-center p-6 max-w-sm">
            <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>Exam Ready</h3>
            <p className="text-sm text-gray-600 mb-1">{questions.length} questions &middot; {exam.config?.difficulty} difficulty</p>
            {exam.config?.timed && (
              <p className="text-sm text-amber-600 mb-4"><Clock className="w-4 h-4 inline mr-1" />{exam.config.time_limit_minutes} minute time limit</p>
            )}
            <Button data-testid="start-exam-btn" onClick={handleStartExam} disabled={starting}
              className="rounded-xl bg-gradient-to-r from-violet-600 to-purple-700 hover:from-violet-700 hover:to-purple-800 h-11 px-8">
              {starting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Starting...</> : 'Begin Exam'}
            </Button>
          </div>
        </div>
      );
    }

    if (!q) return null;

    return (
      <div className="flex flex-col h-full min-h-[400px]">
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Q {currentQ + 1}/{questions.length}</span>
            <div className="w-24 h-2 bg-gray-200 rounded-full">
              <div className="h-full bg-violet-600 rounded-full transition-all" style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }} />
            </div>
            <span className="text-xs text-gray-500">{answeredCount}/{questions.length} answered</span>
          </div>
          {timeLeft !== null && (
            <div className={`flex items-center gap-1 text-sm font-mono font-bold ${timeLeft < 60 ? 'text-red-600' : timeLeft < 300 ? 'text-amber-600' : 'text-gray-700'}`}>
              <Clock className="w-4 h-4" />{formatTime(timeLeft)}
            </div>
          )}
        </div>

        <div className="flex-1 p-5 overflow-y-auto">
          <div className="flex items-center gap-2 mb-1">
            {q.difficulty && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                q.difficulty === 'hard' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
              }`}>{q.difficulty}</span>
            )}
            {q.topic && <span className="text-xs text-gray-400">{q.topic}</span>}
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-4">{q.question}</h3>
          <div className="space-y-2">
            {q.options?.map((opt, idx) => (
              <button key={idx} data-testid={`exam-option-${currentQ}-${idx}`}
                onClick={() => setAnswers({ ...answers, [String(currentQ)]: idx })}
                className={`w-full p-3.5 text-left rounded-xl border-2 transition-all text-sm ${
                  answers[String(currentQ)] === idx ? 'border-violet-500 bg-violet-50 text-violet-800' : 'border-gray-200 hover:border-gray-300 text-gray-700'
                }`}>
                <span className="font-semibold text-gray-500 mr-2">{String.fromCharCode(65 + idx)}.</span>{opt}
              </button>
            ))}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 bg-white flex items-center justify-between gap-2">
          <Button onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0} variant="outline" size="sm" className="rounded-xl">
            <ChevronLeft className="w-4 h-4 mr-1" />Prev
          </Button>
          {currentQ === questions.length - 1 ? (
            <Button data-testid="submit-exam-btn" onClick={handleSubmitExam} disabled={submitting}
              className="rounded-xl bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}Submit Exam
            </Button>
          ) : (
            <Button onClick={() => setCurrentQ(currentQ + 1)} size="sm" className="rounded-xl">Next<ChevronRight className="w-4 h-4 ml-1" /></Button>
          )}
        </div>
      </div>
    );
  }

  // --- RESULTS ---
  if (step === 'results' && results) {
    const { score, correct, total, analysis, recommended_flashcards } = results;
    const weakTopics = analysis?.weak_topics || [];
    const strongTopics = analysis?.strong_topics || [];

    return (
      <div className="p-6 overflow-y-auto max-h-[550px]">
        <div className="text-center mb-6">
          <div className={`w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg ${
            score >= 70 ? 'bg-gradient-to-br from-green-500 to-emerald-600' :
            score >= 50 ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-red-500 to-rose-600'
          }`}>
            <span className="text-3xl font-bold text-white">{score?.toFixed(0)}%</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Crimson Text, serif' }}>
            {score >= 80 ? 'Excellent!' : score >= 70 ? 'Great Job!' : score >= 50 ? 'Good Effort!' : 'Keep Studying!'}
          </h3>
          <p className="text-sm text-gray-500">{correct} of {total} correct</p>
        </div>

        {weakTopics.length > 0 && (
          <div className="mb-5 p-4 bg-red-50 rounded-xl border border-red-200">
            <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Weak Topics — Focus on these</h4>
            <div className="space-y-1.5">
              {weakTopics.map((t, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-red-700">{t.topic}</span>
                  <span className="text-red-600 font-mono font-semibold">{t.correct}/{t.total} ({t.percentage}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {strongTopics.length > 0 && (
          <div className="mb-5 p-4 bg-green-50 rounded-xl border border-green-200">
            <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-1.5"><Check className="w-4 h-4" />Strong Topics</h4>
            <div className="space-y-1.5">
              {strongTopics.map((t, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-green-700">{t.topic}</span>
                  <span className="text-green-600 font-mono font-semibold">{t.correct}/{t.total} ({t.percentage}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {recommended_flashcards?.length > 0 && (
          <div className="mb-5 p-4 bg-violet-50 rounded-xl border border-violet-200">
            <h4 className="text-sm font-semibold text-violet-800 mb-2 flex items-center gap-1.5"><Brain className="w-4 h-4" />Recommended Review ({recommended_flashcards.length} flashcards)</h4>
            <div className="space-y-2">
              {recommended_flashcards.slice(0, 5).map((card, i) => (
                <div key={i} className="p-2 bg-white rounded-lg border border-violet-100 text-sm">
                  <p className="font-medium text-gray-800">{card.front}</p>
                  <p className="text-gray-500 text-xs mt-1">{card.back}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Question Review</h4>
          <div className="space-y-3">
            {results.results?.map((r, i) => (
              <div key={i} className={`p-3 rounded-xl border ${r.is_correct ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-start gap-2">
                  {r.is_correct ? <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> : <X className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />}
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-800">{exam?.questions?.[i]?.question || `Question ${i + 1}`}</p>
                    {!r.is_correct && r.explanation && <p className="text-xs text-gray-600 mt-1">{r.explanation}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Button data-testid="retake-exam-btn" onClick={handleRetake} variant="outline" className="w-full rounded-xl">
          <RotateCcw className="w-4 h-4 mr-2" />New Exam
        </Button>
      </div>
    );
  }

  return null;
};

export default ExamMode;
